import PermissionButton from 'components/buttons/PermissionButton';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import FormRow from 'components/forms/FormRow';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { useIsNewDesignInput } from 'components/forms/inputs/rhf-inputs/newDesignInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import InfoTooltip from 'components/InfoTooltip';
import { PersonalCodeShowLink } from 'components/PersonalCodeShow';
import ContactDynamicMultiSelectInput from 'components/party/ContactDynamicMultiSelectInput';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import { FuzzySearchTableView } from 'hooks/useFuzzySearch';
import { flushSync } from 'react-dom';
import { useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetContactPersonalCodeQuery, useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { Setting } from 'types/back-office-settings';
import { PartyType, RelationshipTypeCode } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { FeatureFlag } from 'types/features';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { isPersonalCodeMask } from 'utils/personalCodeMask';
import { useHasPermissions } from 'utils/permissionUtil';
import { useSettings } from 'utils/useSettings';
import { RelatedKeywordsInput, RelationshipGrowthNotesInput } from './OtherFields';
import { SegmentFields } from './SegmentFields';
import { ContactFormState } from './useContactForm';

/**
 * Person details: names, title, keywords and areas of responsibility, identity code, home address, business areas and
 * contacts, employers.
 */
export const PersonFields = ({ form }: { form: ContactFormState }): JSX.Element => {
    const { control, setValue, setFocus } = form.methods;
    const {
        businessAreaOptions,
        classifiers,
        contact,
        fuzzySearchStates,
        isBusinessAreaRequired,
        isPersonalIdCodeMandatory: isPersonalCodeRequired,
        lastNameFirst,
        openAddNewCompanyContactModal,
        personTitleOptions,
        relationshipType,
        searchHandlers,
        type,
        updateSearchState,
    } = form;
    const isNewDesign = useIsNewDesignInput();
    const isManualPersonTitle = useSettings().isSettingEnabled(Setting.PERSON_TITLES_FREE_TEXT);
    const canShowPersonalCode = useHasPermissions(['READ.PARTIES.UNHIDE_PERSONS_ID_CODE']);
    const [getPersonalCode] = useLazyGetContactPersonalCodeQuery();
    const personalCode = useWatch({ control, name: 'personalCode' });

    return (
        <>
            <FormRow>
                {lastNameFirst ? (
                    <>
                        <TextInput
                            name="lastName"
                            control={control}
                            label="last-name"
                            required
                            placeholder="forms.enter-last-name"
                            maxLength={100}
                        />
                        <TextInput
                            name="firstName"
                            control={control}
                            label="first-name"
                            required
                            placeholder="forms.enter-first-name"
                            maxLength={100}
                        />
                    </>
                ) : (
                    <>
                        <TextInput
                            name="firstName"
                            control={control}
                            label="first-name"
                            required
                            placeholder="forms.enter-first-name"
                            maxLength={100}
                        />
                        <TextInput
                            name="lastName"
                            control={control}
                            label="last-name"
                            required
                            placeholder="forms.enter-last-name"
                            maxLength={100}
                        />
                    </>
                )}
            </FormRow>
            {type === 'edit' && (
                <TextInput
                    name="name"
                    control={control}
                    label="contacts.display-name-unofficial"
                    tooltip={<InfoTooltip text="contacts.tooltips.unofficial-name" />}
                    placeholder={isNewDesign ? undefined : 'forms.enter-name'}
                    required
                    maxLength={150}
                />
            )}
            <FeatureFlagged
                action="show"
                mode="any"
                flag={[FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_CONTACTS]}
            >
                <FormRow>
                    <TextInput
                        name="localFirstName"
                        control={control}
                        label="first-name-local"
                        placeholder="forms.enter-first-name-local"
                        maxLength={100}
                    />
                    <TextInput
                        name="localLastName"
                        control={control}
                        label="last-name-local"
                        placeholder="forms.enter-last-name-local"
                        maxLength={100}
                    />
                </FormRow>
            </FeatureFlagged>
            {/* The add form hides the person's unofficial name field; party.name is auto-filled
                with "First Last" from the effect that watches firstName/lastName. */}
            {/* In the create dialog a duplicates panel is drawn where the field it searches by is: the names
                are always required, so theirs is always under them in Required. */}
            {type === 'add' && (
                <CreateDialogRequiredBlock when>
                    <FuzzySearchTableView
                        fuzzySearchResults={fuzzySearchStates.NAME.results}
                        partyType={PartyType.PERSON}
                        isAccordionExpanded={fuzzySearchStates.NAME.isExpanded}
                        setIsAccordionExpanded={(expanded) => updateSearchState('NAME', { isExpanded: expanded })}
                        classifiers={classifiers}
                    />
                </CreateDialogRequiredBlock>
            )}
            <FormRow>
                {isManualPersonTitle ? (
                    <TextInput control={control} name="personManualTitle" label="title" placeholder="title" />
                ) : (
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="title"
                        name="personTitle"
                        options={personTitleOptions}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="title"
                    />
                )}
                <TextInput
                    name="position"
                    control={control}
                    label="contacts.position"
                    placeholder="contacts.position"
                />
            </FormRow>
            <FormRow>
                <RelatedKeywordsInput form={form} />
                <MultiSelectInput
                    control={control}
                    name="areaOfResponsibilityTypes"
                    label="contacts.areas-of-responsibility"
                    placeholder="contacts.areas-of-responsibility"
                    tooltip={<InfoTooltip text="contacts.tooltips.areas-of-responsibility" />}
                    options={
                        getValidOptionsFromClassifier<string, string>(classifiers['AREA_OF_RESPONSIBILITY_TYPE']) ?? []
                    }
                    setValue={setValue}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                />
            </FormRow>
            <RelationshipGrowthNotesInput form={form} />
            <TextInput
                name="personalCode"
                control={control}
                label="contacts.person-id"
                placeholder="contacts.person-id"
                onChange={searchHandlers?.personalCode?.handler}
                required={isPersonalCodeRequired}
                suffixComponent={
                    contact && canShowPersonalCode && isPersonalCodeMask(personalCode) ? (
                        <PersonalCodeShowLink
                            request={() => getPersonalCode(String(contact.id))}
                            onShow={(code) => {
                                // The link goes with the mask: the field is drawn without it before it takes focus.
                                flushSync(() => setValue('personalCode', code));
                                setFocus('personalCode');
                            }}
                        />
                    ) : undefined
                }
            />
            {type === 'add' && (
                <CreateDialogRequiredBlock when={isPersonalCodeRequired}>
                    <FuzzySearchTableView
                        fuzzySearchResults={fuzzySearchStates.PERSONAL_ID.results}
                        partyType={PartyType.PERSON}
                        isAccordionExpanded={fuzzySearchStates.PERSONAL_ID.isExpanded}
                        setIsAccordionExpanded={(expanded) =>
                            updateSearchState('PERSONAL_ID', { isExpanded: expanded })
                        }
                        classifiers={classifiers}
                    />
                </CreateDialogRequiredBlock>
            )}
            <TextInput name="linkedinUrl" control={control} label="contacts.linkedin" placeholder="contacts.linkedin" />
            <AddressInput
                label="address"
                name="homeAddress"
                control={control}
                classifiers={classifiers}
                customFields={contact?.homeAddress?.customFields as CustomField[]}
            />
            {relationshipType === RelationshipTypeCode.CLIENT ? (
                <>
                    <FeatureFlagged
                        action="show"
                        mode="any"
                        flag={[FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_CONTACTS]}
                    >
                        <AddressInput
                            label="address-local"
                            name="homeLocalAddress"
                            control={control}
                            classifiers={classifiers}
                            customFields={contact?.homeLocalAddress?.customFields as CustomField[]}
                        />
                    </FeatureFlagged>
                    <MultiSelectInput
                        control={control}
                        name="businessAreas"
                        label="business-areas"
                        placeholder="business-areas"
                        options={businessAreaOptions}
                        setValue={setValue}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        sortDirection="asc"
                        required={isBusinessAreaRequired}
                    />
                </>
            ) : null}
            <ContactDynamicMultiSelectInput
                control={control}
                name="businessContacts"
                label="contacts.business-contact-of"
                placeholder="contacts.business-contact-of"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                setValue={setValue}
            />
            <ContactDynamicMultiSelectPicker
                setValue={setValue}
                control={control}
                label="contacts.employed-by"
                name="employedBy"
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    filters: `partyType==${PartyType.COMPANY}`,
                    searchField: ['name', 'partyNumber'],
                }}
                isPickerModalOptionEnabled={(option) => option.partyType === PartyType.COMPANY}
                placeholder="contacts.employed-by"
                noOptionsContent={
                    <PermissionButton
                        permissions={[
                            'CREATE.PARTIES',
                            'CREATE.PARTIES.CLIENTS',
                            'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                        ]}
                        variant="contained"
                        color="secondary"
                        size="extraSmall"
                        onClick={openAddNewCompanyContactModal}
                    >
                        <FormattedMessage id="contacts.add-new-contact" />
                    </PermissionButton>
                }
                PickerModalComponent={ContactPickerModal}
            />
            <SegmentFields form={form} />
        </>
    );
};
